import { useCallback, useMemo, useState } from 'react';
import type { HexAlert, HexCounter } from '../api/contracts';
import { ALL_ALERTS, useAlertTypeFilter, type AlertTypeFilter } from './useAlertTypeFilter';

/**
 * Every filter the alert boards' filter panel offers, decided in ONE place for all three
 * boards: alert type (from `useAlertTypeFilter`), severity, and "only cameras with open alerts".
 *
 * Same contract as the alert-type filter it grows out of — it DIMS, never hides (the site filter
 * on the honeycomb is the one that hides, and it stays with that board), and it answers per
 * ALERT for the panel and per CAMERA for the canvas:
 *
 *   · an alert passes when it matches the chosen type AND one of the chosen severities;
 *   · a camera matches when at least one of its open alerts passes — so "Fire" + "Warning"
 *     lights a camera with a warning-level fire, not one with a critical fire and a warning
 *     loitering;
 *   · "only with open alerts" dims cameras with nothing open. Type and severity already imply
 *     it (a camera with no alerts can't carry a matching one); on its own it is the quick
 *     "show me what's lit" switch.
 *
 * With no type chosen, a camera's COUNTERS also count as evidence: they are what paints the
 * marker red, and the feeds are a paged slice — a camera visibly alerting must not be dimmed
 * by "Critical" just because its alert is on page two of the panel.
 */

export type BoardSeverity = HexAlert['severity'];

/** Panel order, with the chip label and dot colour (the legend's own swatches). */
export const BOARD_SEVERITIES: Array<{ key: BoardSeverity; label: string; dot: string }> = [
  { key: 'critical', label: 'Critical', dot: 'bg-red-600' },
  { key: 'warning', label: 'Warning', dot: 'bg-amber-500' },
  { key: 'normal', label: 'Info', dot: 'bg-blue-600' },
];

export interface BoardFilters {
  /** The alert-type part — its `value` / `setValue` / `options` / `group` drive the radio list. */
  alertType: AlertTypeFilter;
  /** Chosen severities. Empty = every severity. */
  severities: ReadonlySet<BoardSeverity>;
  toggleSeverity: (s: BoardSeverity) => void;
  onlyOpen: boolean;
  setOnlyOpen: (on: boolean) => void;
  /** True when this camera should be greyed out under the current choices. */
  isDimmed: (cameraId: string) => boolean;
  /** Does this ALERT pass type AND severity? What the panel filters by. */
  matches: (alert: Pick<HexAlert, 'alertName' | 'eventType' | 'severity'>) => boolean;
  /** How many in-scope cameras survive. */
  matchCount: number;
  /** How many of the three sections are narrowing the board. */
  activeCount: number;
  /** Reset all three. */
  clear: () => void;
}

export interface UseBoardFiltersOptions {
  recent: HexAlert[];
  cards: HexAlert[];
  /** The cameras this board draws; counts and the match total are relative to these. */
  cameraIds: string[];
  /** The board's per-camera counters — see the header on why they count. */
  counters?: Record<string, HexCounter>;
}

export function useBoardFilters({ recent, cards, cameraIds, counters }: UseBoardFiltersOptions): BoardFilters {
  const alertType = useAlertTypeFilter({ recent, cards, cameraIds });
  const [severities, setSeverities] = useState<ReadonlySet<BoardSeverity>>(() => new Set());
  const [onlyOpen, setOnlyOpen] = useState(false);

  const toggleSeverity = useCallback((s: BoardSeverity) => {
    setSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);

  /** Each camera's open alerts, from both feeds (either can be the only one naming it). */
  const alertsByCamera = useMemo(() => {
    const m = new Map<string, HexAlert[]>();
    const add = (a: HexAlert) => {
      if (!a.cameraId) return;
      const hit = m.get(a.cameraId);
      if (hit) hit.push(a);
      else m.set(a.cameraId, [a]);
    };
    recent.forEach(add);
    cards.forEach(add);
    return m;
  }, [recent, cards]);

  const typeOn = alertType.value !== ALL_ALERTS;
  const sevOn = severities.size > 0;
  const { matches: typeMatches } = alertType;

  const matches = useCallback(
    (a: Pick<HexAlert, 'alertName' | 'eventType' | 'severity'>) =>
      typeMatches(a) && (!sevOn || severities.has(a.severity)),
    [typeMatches, sevOn, severities],
  );

  const isDimmed = useCallback(
    (cameraId: string) => {
      if (!typeOn && !sevOn && !onlyOpen) return false;
      if (alertsByCamera.get(cameraId)?.some(matches)) return false;
      // Without a type there is nothing per-alert left to ask, and the counters know about
      // alerts the feeds haven't paged in yet.
      if (!typeOn) {
        const c = counters?.[cameraId];
        if (c && (sevOn ? [...severities].some((s) => (c[s] || 0) > 0) : c.total > 0)) return false;
      }
      return true;
    },
    [typeOn, sevOn, onlyOpen, alertsByCamera, matches, counters, severities],
  );

  const matchCount = useMemo(
    () => cameraIds.reduce((n, id) => (isDimmed(id) ? n : n + 1), 0),
    [cameraIds, isDimmed],
  );

  const { setValue: setType } = alertType;
  const clear = useCallback(() => {
    setType(ALL_ALERTS);
    setSeverities(new Set());
    setOnlyOpen(false);
  }, [setType]);

  return {
    alertType,
    severities,
    toggleSeverity,
    onlyOpen,
    setOnlyOpen,
    isDimmed,
    matches,
    matchCount,
    activeCount: (typeOn ? 1 : 0) + (sevOn ? 1 : 0) + (onlyOpen ? 1 : 0),
    clear,
  };
}

export default useBoardFilters;
